import type { MouseEventHandler, PropsWithChildren } from 'react';
import { twJoin } from 'tailwind-merge';

import { Link } from '@/core/routing/components/Link';
import { Span } from '@/core/tamagoshiTailwind/components/Typography/Typography';

type PaginationItemProps = PropsWithChildren<{
  page: number;
  current: boolean;
  count: number;
  hrefForPage?: (page: number) => string;
  onPageChange?: (page: number) => void;
  disabled: boolean;
  hiddenInMobile?: boolean;
  ariaLabel?: string;
}>;

export function PaginationItem({
  current,
  page,
  count,
  onPageChange,
  hrefForPage,
  disabled,
  children,
  hiddenInMobile,
  ariaLabel,
}: PaginationItemProps) {
  const safePage = page < 1 ? 1 : page > count ? count : page;
  const effectiveDisabled = disabled || safePage !== page;

  const handleOnClick: MouseEventHandler = (event) => {
    if (effectiveDisabled || current) {
      event.preventDefault();
    } else {
      onPageChange?.(safePage);
    }
  };

  const commonProps = {
    className: twJoin(
      `flex size-xl items-center justify-center rounded-card
      data-[current]:cursor-default data-[disabled]:cursor-not-allowed
      data-[current]:bg-foundation-quinary
      data-[current]:text-foundation-inverted
      data-[disabled]:[&:not([data-current])]:text-foundation-disabled
      hover:[&:not([data-disabled])]:[&:not([data-current])]:bg-foundation-primary`,
    ),
    'data-current': current || undefined,
    'data-disabled': effectiveDisabled || undefined,
    'aria-label': ariaLabel,
  };

  const href = hrefForPage?.(safePage);

  return (
    <li className={twJoin(hiddenInMobile && 'hidden tablet:block')}>
      {href ? (
        <Link href={href} onClick={handleOnClick} {...commonProps}>
          {children}
        </Link>
      ) : (
        <button
          type="button"
          disabled={effectiveDisabled}
          onClick={() => onPageChange?.(safePage)}
          {...commonProps}
        >
          {children}
        </button>
      )}
    </li>
  );
}

export function PaginationPageItem({
  children,
  ...props
}: PaginationItemProps) {
  return (
    <PaginationItem {...props}>
      <Span weight="semibold" scale="body2">
        {children}
      </Span>
    </PaginationItem>
  );
}
